import {
  forwardRef,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type MouseEventHandler,
} from "react";
import styled from "styled-components";

import { InputControl } from "../Input/foundations/inputStyles";
import type { InputSize } from "../Input/foundations/inputTypes";
import { inlineElementIndicatorSize } from "../foundations/controlStyles";

/**
 * "값 필드 → 팝업" 피커 서브패밀리(DatePicker/DateRangePicker/TimeInput) 공용 트리거 **UI**.
 * 트리거는 사실상 "trailing 아이콘이 붙은 Input 필드"라 Input family의 필드 shell(`InputControl`)을
 * `<button>`으로 재사용한다 — 테두리/hover/focus/disabled/size 토큰을 c6 텍스트 필드와 100% 공유
 * (재발명·시각 불일치 방지). 토글 로직은 도메인 무관이라 `foundations/popoverTrigger`의
 * `useTriggerToggle`을 쓴다(여기 아님).
 */

/**
 * 커스텀 트리거 render-slot API. 소비자는 기본 트리거 대신 자기 요소(예: IconButton)로 팝업을
 * 열고, 값은 onChange로 받는다. `triggerProps`를 요소에 spread하면 토글 + aria가 붙는다.
 * (`triggerProps`/`open`/`close`는 `useTriggerToggle` 반환과 동일 형태 + picker 값(`value`).)
 */
export interface PickerTriggerApi<TValue> {
  triggerProps: {
    onClick: MouseEventHandler<HTMLElement>;
    "aria-haspopup": "dialog";
    "aria-expanded": boolean;
  };
  /** 클릭한 요소를 anchor로 팝업을 연다(직접 제어가 필요할 때). */
  open: (event: MouseEvent<HTMLElement>) => void;
  /** 팝업을 닫는다. */
  close: () => void;
  isOpen: boolean;
  value: TValue;
  disabled: boolean;
}

/** 공용 컨테이너. */
export const PickerRoot = styled.div`
  display: inline-flex;
  min-width: 0;
`;

/**
 * Input 필드 shell(`InputControl`)을 button으로 확장한 트리거 표면. 값 표시(span) + trailing 아이콘,
 * 미선택 시 placeholder 톤(text.subtle). 테두리/hover/size/transparent 배경은 `InputControl`에서
 * 그대로 상속하고, button 고유의 focus(:focus-visible → border.focus)·disabled만 얹는다
 * (컨테이너 border 류 focus 규약 = RFC-semantic-token-map §focus, §5-A·§5-C).
 */
const PickerTriggerSurface = styled(InputControl)<{
  $placeholder?: boolean;
  $indicatorSize: InputSize;
}>`
  min-width: 14rem;
  justify-content: space-between;
  cursor: pointer;
  text-align: left;
  /* InputControl의 size별 공용 font-size/line-height를 그대로 상속한다. 값/placeholder script가
     달라도 결정적 line box라 트리거 높이가 흔들리지 않는다. */
  color: ${({ theme, $placeholder }) =>
    $placeholder ? theme.color.text.subtle : theme.color.text.default};

  &:focus-visible {
    border-color: ${({ theme }) => theme.color.border.focus};
    outline: none;
  }

  &:disabled {
    pointer-events: none;
    background-color: ${({ theme }) => theme.color.bg.surfaceSunken};
    border-color: ${({ theme }) => theme.color.border.disabled};
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }
  /* disabled면 값 텍스트·아이콘을 함께 dim (아이콘이 자체 색을 명시하므로 여기서 덮어써 통일). */
  &:disabled > svg {
    color: ${({ theme }) => theme.color.text.disabled};
  }

  & > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > svg {
    flex: 0 0 auto;
    color: ${({ theme }) => theme.color.text.subtle};
    font-size: ${({ $indicatorSize }) => inlineElementIndicatorSize[$indicatorSize]};
  }
`;

export type PickerTriggerProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** 미선택(placeholder) 톤 여부. */
  placeholder?: boolean;
  /** 필드 크기 — Input 필드 shell과 같은 스케일(sm 28 / md 32). 기본 md, dense bar는 sm. */
  size?: InputSize;
};

/** 공용 트리거 버튼(선택 결과 표시 + 팝업 오픈). Input 필드 shell 재사용. */
export const PickerTrigger = forwardRef<HTMLButtonElement, PickerTriggerProps>(
  ({ placeholder = false, size = "md", children, ...rest }, ref) => (
    <PickerTriggerSurface
      as="button"
      type="button"
      ref={ref}
      $placeholder={placeholder}
      $indicatorSize={size}
      $fullWidth={false}
      $inputSize={size}
      {...rest}
    >
      {children}
    </PickerTriggerSurface>
  ),
);

PickerTrigger.displayName = "PickerTrigger";
